import { Grid } from '@mui/material'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import { getLocalizedRoleName } from '../../../common/roleHelper'
import { getErrMsg, toastNeg, toastPos } from '../../../common/toastHelper'
import { IDropdownProjects, IProjectPersonDetails, IUserProject } from '../../../interfaces/projects/IProjects'
import { ILinkedProject, ISkillProject } from '../../../interfaces/skills/ISkills'
import { useStore } from '../../../Provider'
import { ExitConfirmation } from '../../OPcomponents/ExitConfirmation'
import { OPLoadingCircle } from '../../OPcomponents/OPLoadingcircle'
import { OPSkillsCombobox } from '../../OPcomponents/OPSkillsCombobox'
import '../../styles/grid.scss'
import { OPSkillsButtons } from '../OPSkillsButtons'
import '../profile.scss'

const ProjectSkillLink = (props: {
  setLinkingProject: (val: boolean) => void
  setLinkedProjects: (projets: ILinkedProject[]) => void
}): JSX.Element => {
  const { setLinkingProject, setLinkedProjects } = props
  const rootStore = useStore()
  const { t } = useTranslation()
  const { validatedUser } = rootStore.loginStore
  const { editCurrentSkill, postNewSkillProject, getUserDetailedSkillLinkedProjects, setShowAddProjectToSkillModal, editCurrentProject } =
    rootStore.skillStore

  const {
    // Only selection is allowed in this view; we don't edit or create user projects here
    showAddSkillToProjectModal,
    projects,

    getDetailedUserProjects,
    clearUserProjectDetails,
    loadingProjectPersonDetails,
  } = rootStore.projectStore
  const { userObject, getUserOverview } = rootStore.personStore

  const [chosenDropdownProject, setChosenDropdownProject] = useState<IDropdownProjects>()
  const [selectedProject, setSelectedProject] = useState<IProjectPersonDetails>()

  useEffect(() => {
    // Ensure details are cleared on unmount
    return () => {
      clearUserProjectDetails()
    }
  }, [clearUserProjectDetails])

  useEffect(() => {
    return () => {
      clearUserProjectDetails()
      setShowAddProjectToSkillModal(false)
    }
  }, [clearUserProjectDetails, setShowAddProjectToSkillModal])

  useEffect(() => {
    const fetchOverview = async () => {
      if (isEmpty(userObject)) {
        await getUserOverview(validatedUser)
      }
    }
    fetchOverview()
  }, [getUserOverview, userObject, validatedUser])

  // Korjaa tämä + antin viesit
  const filteredDropdownProjects = useMemo(() => {
    return projects
      .filter((proj) => Boolean(proj.userHas))
      .filter((i) => editCurrentProject?.every((linkedProject) => linkedProject.projectId !== i.project.projectId))
      .map((project) => ({
        key: project.project.projectId,
        value: String(project.project.projectId),
        text: project.project.name + (project.project.customer ? ` (${project.project.customer})` : ''),
        isEmploymentProject: project.isEmployerJobTitlesProject,
      }))
      .sort((a, b) => {
        if (a.isEmploymentProject !== b.isEmploymentProject) {
          return a.isEmploymentProject ? -1 : 1
        }
        return a.text.localeCompare(b.text, getI18n().language, { sensitivity: 'base' })
      })
  }, [projects, editCurrentProject])

  const initValues: IUserProject = {
    userId: validatedUser,
    manDays: selectedProject?.manDays ?? 0,
    manMonths: selectedProject?.manMonths ?? 0,
    projectId: selectedProject?.projectId ?? (chosenDropdownProject?.key || -1),
    startDate: selectedProject?.startDate,
    endDate: selectedProject?.endDate,
    usingManDays: selectedProject?.usingManDays ? true : false,
    usingManMonths: false,
    descriptionFi: selectedProject?.descriptionFi ?? '',
    descriptionEng: selectedProject?.descriptionEng ?? '',
    roleIds:
      selectedProject?.roles.map((role) => ({ value: role.roleId.toString(), key: role.roleId, text: getLocalizedRoleName(role) })) ?? [],
  }

  // Only project selection is allowed; no workload mode selection needed

  const submitForm = async (values: IUserProject) => {
    const newPersonSkillProject: ISkillProject = {
      userId: validatedUser,
      projectId: values.projectId,
      skillId: editCurrentSkill.skillId,
    }

    try {
      // Only create the skill-project link; do not modify project details here
      await postNewSkillProject(newPersonSkillProject)
      toastPos(t('Toasts.SkillProjectAdd'), true)
    } catch (err) {
      toastNeg(getErrMsg(err, t) || t('Toasts.SkillProjectAddError'), true)
    } finally {
      await handleClose()
    }
  }

  const handleClose = async () => {
    setLinkingProject(false)
    const projectLinks = await getUserDetailedSkillLinkedProjects(validatedUser, editCurrentSkill.skillId)
    setLinkedProjects(projectLinks.skill.linkedProjects)
  }

  const onProjectChange = async (projectId: number) => {
    const changedProject = await getDetailedUserProjects(validatedUser, projectId)
    setSelectedProject(changedProject)
  }

  return (
    <Grid container className='gridcontainer--formcontainer__itempaddings'>
      {loadingProjectPersonDetails ? (
        <Grid xs={12} item>
          <OPLoadingCircle />
        </Grid>
      ) : (
        <Formik enableReinitialize initialValues={initValues} onSubmit={(values) => submitForm(values)}>
          {({ setFieldValue, isSubmitting, handleSubmit, dirty }) => (
            <Grid container paddingBottom={2}>
              <ExitConfirmation when={dirty} />
              <Grid xs={12} item className='griditem--formitem__titlemargin'>
                <span className='myskill--font__titlefont sini'>{t('Skills.AddProject')}</span>
              </Grid>
              <Grid container rowSpacing={4} columnSpacing={4}>
                <Grid xs={12} lg={6} item>
                  <Grid container spacing={4}>
                    <Grid xs={12} item>
                      <OPSkillsCombobox
                        name='projectId'
                        label={`${t('Projects.EmployerOrProject')} *`}
                        optionList={filteredDropdownProjects}
                        curState={chosenDropdownProject}
                        groupBy={(opt) => (opt?.isEmploymentProject ? t('Projects.Employer') : t('Projects.Project'))}
                        onChange={(_e, val) => {
                          setChosenDropdownProject(val)
                          onProjectChange(val.value)
                        }}
                        onDelete={() => {
                          setChosenDropdownProject(undefined)
                          setFieldValue('projectId', null)
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <OPSkillsButtons
                  noPadding
                  bothDisabled={showAddSkillToProjectModal || isSubmitting || !chosenDropdownProject}
                  disabled={!chosenDropdownProject || showAddSkillToProjectModal || isSubmitting}
                  onClose={handleClose}
                  onSubmit={handleSubmit}
                />
              </Grid>
            </Grid>
          )}
        </Formik>
      )}
    </Grid>
  )
}

export default observer(ProjectSkillLink)
